import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslocoService } from '@jsverse/transloco';

import { RessourcesModule } from '@ressources/client/ressources.module';

export enum LayerVisibilityButtonState {
    HiddenDisabled = 'HiddenDisabled',
    HiddenEnabled = 'HiddenEnabled',
    VisibleDisabled = 'VisibleDisabled',
    VisibleEnabled = 'VisibleEnabled',
}

type VisibilityViewModel = {
    icon: IconProp;
    textBlack: boolean;
    textDisabledColor: boolean;
};

const VISIBILITY_VIEW_MODEL_BY_STATE: Record<LayerVisibilityButtonState, VisibilityViewModel> = {
    [LayerVisibilityButtonState.HiddenDisabled]: {
        icon: ['fas', 'eye-slash'],
        textBlack: false,
        textDisabledColor: true,
    },
    [LayerVisibilityButtonState.HiddenEnabled]: {
        icon: ['fas', 'eye-slash'],
        textBlack: true,
        textDisabledColor: false,
    },
    [LayerVisibilityButtonState.VisibleDisabled]: {
        icon: ['fas', 'eye'],
        textBlack: false,
        textDisabledColor: true,
    },
    [LayerVisibilityButtonState.VisibleEnabled]: {
        icon: ['fas', 'eye'],
        textBlack: true,
        textDisabledColor: false,
    },
};

const TOOLTIP_BY_TYPE: {
    group: Record<LayerVisibilityButtonState, string | null>;
    unit: Record<LayerVisibilityButtonState, string | null>;
} = {
    group: {
        [LayerVisibilityButtonState.HiddenDisabled]: 'themeInactiveAndOutOfZoomRange',
        [LayerVisibilityButtonState.HiddenEnabled]: null,
        [LayerVisibilityButtonState.VisibleDisabled]: 'themeActiveButOutOfZoomRange',
        [LayerVisibilityButtonState.VisibleEnabled]: null,
    },
    unit: {
        [LayerVisibilityButtonState.HiddenDisabled]: 'layerInactiveAtThisZoomLevel',
        [LayerVisibilityButtonState.HiddenEnabled]: null,
        [LayerVisibilityButtonState.VisibleDisabled]: 'layerActiveButOutOfZoomRange',
        [LayerVisibilityButtonState.VisibleEnabled]: null,
    },
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-visibility-button',
    templateUrl: './visibility-button.component.html',
})
export class VisibilityButtonComponent {
    public readonly canToggle = input<boolean>(true);
    public readonly cursor = signal<'pointer' | 'not-allowed'>('not-allowed');
    public readonly customToolTip = input<string | undefined>(undefined);
    public readonly icon = signal<IconProp>(['fas', 'eye-slash']);
    public readonly isGroup = input<boolean>(false);
    public readonly isTextBlack = signal<boolean>(false);
    public readonly isTextDisabledColor = signal<boolean>(true);
    public readonly tooltip = signal<string>('');
    public readonly visibilityButtonState = input<LayerVisibilityButtonState>(
        LayerVisibilityButtonState.HiddenDisabled,
    );
    public visibilityToggle = output<MouseEvent>();

    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        effect(() => {
            this.updateViewModel();
        });
    }

    public onClick(event: MouseEvent): void {
        if (!this.canToggle()) {
            return;
        }

        this.visibilityToggle.emit(event);
    }

    private updateViewModel(): void {
        const config = VISIBILITY_VIEW_MODEL_BY_STATE[this.visibilityButtonState()];
        let tooltipKey = this.isGroup()
            ? TOOLTIP_BY_TYPE.group[this.visibilityButtonState()]
            : TOOLTIP_BY_TYPE.unit[this.visibilityButtonState()];

        if (this.customToolTip()) {
            tooltipKey = this.customToolTip()!;
        }

        this.icon.set(config.icon);
        this.cursor.set(this.canToggle() ? 'pointer' : 'not-allowed');
        this.isTextBlack.set(config.textBlack);
        this.isTextDisabledColor.set(config.textDisabledColor);
        this.tooltip.set(tooltipKey ? this.translocoService.translate(tooltipKey) : '');
    }
}
